import React, { useState, useEffect, useMemo } from 'react';
import { Student } from '../types';
import { getStudents, addStudent, updateStudent, deleteStudent, getFileUrl, StudentDataWithFiles } from '../services/studentService';
import { toast } from 'react-hot-toast';
import { Eye, Edit, Trash2, Download, Search, Filter } from 'lucide-react';


interface StudentFormData {
  name: string;
  email: string;
  register_num: string;
  dob: string;
  gender: 'male' | 'female';
  level: string;
  group: string;
  student_photo_url: string | File | null;
  birth_certificate: string | File | null;
  reg_no: string;
  address: string;
  fathers_name: string;
  father_num: string;
  father_photo_url: string | File | null;
  mothers_name: string;
  mother_num: string;
  mother_photo_url: string | File | null;
  family_photo_url: string | File | null;
  guardian_name: string;
  guardian_photo_url: string | File | null;
  special_instruction: string;
  allergies: string;
  cab: 'yes' | 'no';
  cab_loc: string;
  day_care: 'yes' | 'no';
  day_care_time: string;
  total_fees: string;
  total_day_care_fees: string;
  total_cab_fees: string;
}



// Utility to always format dob as YYYY-MM-DD
const formatDobForInput = (dob: string | null | undefined): string => {
  if (!dob) return '';
  if (dob.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(dob)) return dob; // already correct
  try {
    const date = new Date(dob);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  } catch {}
  return '';
};

interface FileState {
  student_photo: File | null;
  birth_certificate: File | null;
  father_photo: File | null;
  mother_photo: File | null;
  family_photo: File | null;
  guardian_photo: File | null;
}

interface PreviewImage {
  url: string;
  title: string;
}

function prepareStudentDataForAppwrite(formData: StudentFormData, files: FileState): StudentDataWithFiles {
  let totalFeesInt = Math.floor(Number(formData.total_fees));
  if (isNaN(totalFeesInt) || totalFeesInt < 0) totalFeesInt = 0;
  let totalDayCareFeesInt = Math.floor(Number(formData.total_day_care_fees));
  if (isNaN(totalDayCareFeesInt) || totalDayCareFeesInt < 0) totalDayCareFeesInt = 0;
  let totalCabFeesInt = Math.floor(Number(formData.total_cab_fees));
  if (isNaN(totalCabFeesInt) || totalCabFeesInt < 0) totalCabFeesInt = 0;

  return {
    ...formData,
    // For file fields, pass the File object if it exists, otherwise pass the existing string ID
    student_photo_url: files.student_photo || formData.student_photo_url,
    birth_certificate: files.birth_certificate || formData.birth_certificate,
    father_photo_url: files.father_photo || formData.father_photo_url,
    mother_photo_url: files.mother_photo || formData.mother_photo_url,
    family_photo_url: files.family_photo || formData.family_photo_url,
    guardian_photo_url: files.guardian_photo || formData.guardian_photo_url,
    level: formData.level.toString(),
    total_fees: totalFeesInt.toString(),
    total_day_care_fees: totalDayCareFeesInt.toString(),
    total_cab_fees: totalCabFeesInt.toString(),
  } as StudentDataWithFiles;
}

const StudentPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [search, setSearch] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showEditStudentModal, setShowEditStudentModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [viewingStudent, setViewingStudent] = useState<Student | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [previewImage, setPreviewImage] = useState<PreviewImage | null>(null);
  const [formData, setFormData] = useState<StudentFormData>({
    name: '',
    email: '',
    register_num: '',
    dob: '',
    gender: 'male',
    student_photo_url: null,
    birth_certificate: null,
    reg_no: '',
    address: '',
    fathers_name: '',
    father_num: '',
    father_photo_url: null,
    mothers_name: '',
    mother_num: '',
    mother_photo_url: null,
    family_photo_url: null,
    guardian_name: '',
    guardian_photo_url: null,
    special_instruction: '',
    allergies: '',
    cab: 'no',
    cab_loc: '',
    day_care: 'no',
    day_care_time: '',
    level: '1',
    group: 'butterfly',
    total_fees: '',
    total_day_care_fees: '',
    total_cab_fees: ''
  });
  
  const [files, setFiles] = useState<FileState>({
    student_photo: null,
    birth_certificate: null,
    father_photo: null,
    mother_photo: null,
    family_photo: null,
    guardian_photo: null
  });
  
  const fetchStudents = async () => {
    try {
      setLoading(true);
      const fetchedStudents = await getStudents();
      setStudents(fetchedStudents);
      setError(null);
    } catch (err) {
      console.error('Error fetching students:', err);
      setError('Failed to fetch students. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  // Filter students based on search and grade filter
  const filteredStudents = useMemo(() => {
    return students.filter(student => {
      // Search by name (case insensitive)
      const searchLower = search.toLowerCase();
      const matchesSearch = search === '' || 
        student.name.toLowerCase().includes(searchLower);

      // Filter by level
      const matchesLevel = selectedGrade === 'all' || 
        student.level.toString() === selectedGrade;

      // Filter by group
      const matchesGroup = selectedGroup === 'all' || 
        student.group === selectedGroup;

      return matchesSearch && matchesLevel && matchesGroup;
    });
  }, [students, search, selectedGrade, selectedGroup]);
  
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      register_num: '',
      dob: '',
      gender: 'male',
      student_photo_url: null,
      birth_certificate: null,
      reg_no: '',
      address: '',
      fathers_name: '',
      father_num: '',
      father_photo_url: null,
      mothers_name: '',
      mother_num: '',
      mother_photo_url: null,
      family_photo_url: null,
      guardian_name: '',
      guardian_photo_url: null,
      special_instruction: '',
      allergies: '',
      cab: 'no',
      cab_loc: '',
      day_care: 'no',
      day_care_time: '',
      level: '1',
      group: 'butterfly',
      total_fees: '',
      total_day_care_fees: '',
      total_cab_fees: ''
    });
    setFiles({
      student_photo: null,
      birth_certificate: null,
      father_photo: null,
      mother_photo: null,
      family_photo: null,
      guardian_photo: null
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: keyof FileState) => {
    const file = e.target.files?.[0];
    if (file) {
      console.log(`File selected for ${field}:`, {
        name: file.name,
        type: file.type,
        size: file.size
      });
      
      // Update files state with the File object
      setFiles(prev => {
        const newFiles = {
          ...prev,
          [field]: file
        };
        console.log('Updated files state:', newFiles);
        return newFiles;
      });

      // Create a preview URL for immediate display
      const previewUrl = URL.createObjectURL(file);
      console.log('Created preview URL:', previewUrl);
      
      // Update formData with the File object
      setFormData(prev => {
        const newFormData = {
          ...prev,
          [`${field.replace('_photo', '_photo_url')}`]: file
        };
        console.log('Updated formData state:', newFormData);
        return newFormData;
      });
    }
  };

  const getPhotoUrl = (field: keyof StudentFormData): string | null => {
    // First, check if a new file is selected in the files state
    const fileKey = field.replace('_photo_url', '_photo') as keyof FileState;
    const file = files[fileKey];
    if (file) {
      // If a new file is selected, show its preview
      return URL.createObjectURL(file);
    }
    // Otherwise, use the value from formData (should be a string id or null)
    const value = formData[field];
    if (typeof value === 'string' && value) {
      return getFileUrl(value);
    }
    return null;
  };

  const renderPhotoPreview = (field: keyof StudentFormData, label: string) => {
    const photoUrl = getPhotoUrl(field);
    console.log(`Rendering photo preview for ${field}:`, photoUrl);
    
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        <div className="flex items-center space-x-4">
          <div className="relative w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={label}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = ''; // Clear the src on error
                  e.currentTarget.style.display = 'none'; // Hide the image
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
          </div>
          <div>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e, field.replace('_photo_url', '_photo') as keyof FileState)}
              className="hidden"
              id={`${field}-input`}
            />
            <label
              htmlFor={`${field}-input`}
              className="cursor-pointer inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {photoUrl ? 'Change Photo' : 'Upload Photo'}
            </label>
          </div>
        </div>
      </div>
    );
  };

  const handleAddStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError(null);
      
      // Create student data with File objects
      const studentData = prepareStudentDataForAppwrite(formData, files);

      console.log('Submitting student data:', studentData);
      if (studentData.total_fees && !/^[0-9]+$/.test(studentData.total_fees)) {
        toast.error('Total Fees must be a valid number if provided.');
        setSubmitting(false);
        return;
      }
      await addStudent(studentData);
      toast.success('Student added successfully');
      setShowAddStudentModal(false);
      resetForm();
      fetchStudents();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add student';
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleEditStudent = (student: Student) => {
    console.log('Editing student:', student);
    setEditingStudent(student);
    setFormData({
      name: student.name || '',
      email: student.email || '',
      register_num: student.register_num || '',
      dob: formatDobForInput(student.dob),
      gender: student.gender || 'male',
      address: student.address || '',
      fathers_name: student.fathers_name || '',
      father_num: student.father_num || '',
      mothers_name: student.mothers_name || '',
      mother_num: student.mother_num || '',
      guardian_name: student.guardian_name || '',
      level: student.level || '1',
      group: student.group || 'butterfly',
      cab: student.cab || 'no',
      cab_loc: student.cab_loc || '',
      day_care: student.day_care || 'no',
      day_care_time: student.day_care_time || '',
      special_instruction: student.special_instruction || '',
      student_photo_url: student.student_photo_url || null,
      father_photo_url: student.father_photo_url || null,
      mother_photo_url: student.mother_photo_url || null,
      family_photo_url: student.family_photo_url || null,
      guardian_photo_url: student.guardian_photo_url || null,
      birth_certificate: student.birth_certificate || null,
      reg_no: student.reg_no || '',
      allergies: student.allergies || '',
      total_fees: student.total_fees || '',
      total_day_care_fees: student.total_day_care_fees || '',
      total_cab_fees: student.total_cab_fees || ''
    });
    // Reset files state when editing a new student
    setFiles({
      student_photo: null,
      birth_certificate: null,
      father_photo: null,
      mother_photo: null,
      family_photo: null,
      guardian_photo: null
    });
    setShowEditStudentModal(true);
  };

  const handleUpdateStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingStudent) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      // Log the current state of files and formData
      console.log('Current files state:', files);
      console.log('Current formData state:', formData);
      
      // Create student data with File objects
      const studentData = prepareStudentDataForAppwrite(formData, files);

      // Log the data being sent to updateStudent
      console.log('Updating student with data:', {
        studentId: editingStudent.id,
        studentData: studentData,
        files: files
      });
      
      if (studentData.total_fees && !/^[0-9]+$/.test(studentData.total_fees)) {
        toast.error('Total Fees must be a valid number if provided.');
        setSubmitting(false);
        return;
      }
      if (studentData.total_day_care_fees && !/^[0-9]+$/.test(studentData.total_day_care_fees)) {
        toast.error('Total Day Care Fees must be a valid number if provided.');
        setSubmitting(false);
        return;
      }
      if (studentData.total_cab_fees && !/^[0-9]+$/.test(studentData.total_cab_fees)) {
        toast.error('Total Cab Fees must be a valid number if provided.');
        setSubmitting(false);
        return;
      }
      
      await updateStudent(editingStudent.id, studentData);
      toast.success('Student updated successfully');
      setShowEditStudentModal(false);
      resetForm();
      fetchStudents();
    } catch (error) {
      console.error('Error updating student:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update student';
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>, field: keyof typeof formData) => {
    let value = e.target.value;
    
    // Handle special cases
    if (field === 'dob') {
      value = formatDobForInput(value);
    } else if (field === 'group') {
      value = value || 'butterfly'; // Ensure group always has a value
    } else if (field === 'level') {
      value = value || '1'; // Ensure level always has a value
    } else if (field === 'total_fees') {
      value = value.replace(/[^\d]/g, '');
    } else if (field === 'total_day_care_fees') {
      value = value.replace(/[^\d]/g, '');
    } else if (field === 'total_cab_fees') {
      value = value.replace(/[^\d]/g, '');
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGenderChange = (value: 'male' | 'female') => {
    setFormData(prev => ({
      ...prev,
      gender: value
    }));
  };

  const handleNeedsCabChange = (value: boolean) => {
    setFormData(prev => ({
      ...prev,
      cab: value ? 'yes' : 'no'
    }));
  };

  const handleNeedsDayCareChange = (value: boolean) => {
    setFormData(prev => ({
      ...prev,
      day_care: value ? 'yes' : 'no'
    }));
  };
  
  const handleViewClick = (student: Student) => {
    setViewingStudent(student);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewingStudent(null);
  };

  const handleDeleteStudent = async (student: Student) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        setError('');
        await deleteStudent(student.id);
        setStudents(prev => prev.filter(s => s.id !== student.id));
        setSuccessMessage('Student deleted successfully!');
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          setSuccessMessage('');
        }, 3000);
      } catch (error) {
        console.error('Error deleting student:', error);
        setError('Failed to delete student. Please try again.');
      }
    }
  };
  
  const handleExport = async () => {
    try {
      // Show loading state
      setLoading(true);
      
      // Create CSV content with headers and styling
      const headers = [
        'STUDENT INFORMATION',
        'CONTACT DETAILS',
        'PARENTS INFORMATION',
        'ADDITIONAL INFORMATION'
      ];

      const subHeaders = [
        ['Name', 'Register Number', 'Level', 'Group', 'Gender', 'Date of Birth'],
        ['Email', 'Address'],
        ['Father Name', 'Father Mobile', 'Mother Name', 'Mother Mobile', 'Guardian Name'],
        ['Special Instructions', 'Allergies', 'Cab Service', 'Cab Location', 'Day Care', 'Day Care Timings']
      ];

      // Create rows with student data
      const rows = filteredStudents.map(student => [
        // Student Information
        student.name,
        student.register_num,
        `Level ${student.level}`,
        student.group,
        student.gender,
        new Date(student.dob).toLocaleDateString(),
        // Contact Details
        student.email,
        student.address,
        // Parents Information
        student.fathers_name,
        student.father_num,
        student.mothers_name,
        student.mother_num,
        student.guardian_name,
        // Additional Information
        student.special_instruction || 'None',
        student.allergies || 'None',
        student.cab,
        student.cab_loc || 'N/A',
        student.day_care,
        student.day_care_time || 'N/A'
      ]);

      // Convert to CSV format with styling
      const csvContent = [
        // Main Headers
        headers.join(','),
        // Sub Headers
        subHeaders.flat().join(','),
        // Data Rows
        ...rows.map(row => row.map(cell => {
          const escapedCell = String(cell).replace(/"/g, '""');
          return `"${escapedCell}"`;
        }).join(','))
      ].join('\n');

      // Create a new JSZip instance
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      // Add CSV file to zip
      zip.file('student_details.csv', csvContent);

      // Download and add all files to zip
      const downloadPromises = filteredStudents.map(async (student) => {
        const studentName = student.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        const studentFolder = zip.folder(studentName);
        
        if (!studentFolder) return;

        // Helper function to download and add file to zip
        const downloadAndAddFile = async (fileId: string | null, type: string) => {
          if (!fileId) return;
          
          try {
            const url = getFileUrl(fileId);
            if (!url) return;
            const response = await fetch(url);
            const blob = await response.blob();
            const fileName = `${type}.${blob.type.split('/')[1]}`;
            studentFolder.file(fileName, blob);
          } catch (error) {
            console.error(`Error downloading ${type} file for ${student.name}:`, error);
          }
        };

        // Download all files for this student
        await Promise.all([
          downloadAndAddFile(student.student_photo_url, 'student_photo'),
          downloadAndAddFile(student.birth_certificate, 'birth_certificate'),
          downloadAndAddFile(student.father_photo_url, 'father_photo'),
          downloadAndAddFile(student.mother_photo_url, 'mother_photo'),
          downloadAndAddFile(student.family_photo_url, 'family_photo'),
          downloadAndAddFile(student.guardian_photo_url, 'guardian_photo')
        ]);
      });

      // Wait for all downloads to complete
      await Promise.all(downloadPromises);

      // Generate the zip file
      const content = await zip.generateAsync({ type: 'blob' });

      // Create download link
      const link = document.createElement('a');
      const url = URL.createObjectURL(content);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `student_details_${new Date().toISOString().split('T')[0]}.zip`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Show success message
      setSuccessMessage('Student details exported successfully!');
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setSuccessMessage('');
      }, 3000);

    } catch (error) {
      console.error('Error exporting student details:', error);
      setError('Failed to export student details. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Remove the isFormValid check
  const isFormValid = useMemo(() => {
    return true; // Always return true since we removed required fields
  }, []);
  
  const handleEditClick = (student: Student) => {
    setEditingStudent(student);
    setFormData({
      name: student.name || '',
      email: student.email || '',
      register_num: student.register_num || '',
      dob: formatDobForInput(student.dob),
      gender: student.gender || 'male',
      address: student.address || '',
      fathers_name: student.fathers_name || '',
      father_num: student.father_num || '',
      mothers_name: student.mothers_name || '',
      mother_num: student.mother_num || '',
      guardian_name: student.guardian_name || '',
      level: student.level || '1',
      group: student.group || 'butterfly',
      cab: student.cab || 'no',
      cab_loc: student.cab_loc || '',
      day_care: student.day_care || 'no',
      day_care_time: student.day_care_time || '',
      special_instruction: student.special_instruction || '',
      student_photo_url: student.student_photo_url || null,
      father_photo_url: student.father_photo_url || null,
      mother_photo_url: student.mother_photo_url || null,
      family_photo_url: student.family_photo_url || null,
      guardian_photo_url: student.guardian_photo_url || null,
      birth_certificate: student.birth_certificate || null,
      reg_no: student.reg_no || '',
      allergies: student.allergies || '',
      total_fees: student.total_fees || '',
      total_day_care_fees: student.total_day_care_fees || '',
      total_cab_fees: student.total_cab_fees || ''
    });
    setShowEditStudentModal(true);
    setShowAddStudentModal(false);
  };
  
  // Add this new component for image preview
  const ImagePreviewModal = () => {
    if (!previewImage) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50" onClick={() => setPreviewImage(null)}>
        <div className="relative max-w-4xl max-h-[90vh] p-4">
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-2 right-2 text-white hover:text-gray-300"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="text-white text-center mb-4">{previewImage.title}</div>
          <img
            src={previewImage.url}
            alt={previewImage.title}
            className="max-w-full max-h-[80vh] object-contain"
          />
        </div>
      </div>
    );
  };
  
  // Add this function to handle file preview
  const handleFilePreview = (fileId: string, title: string) => {
    if (!fileId) return;
    const url = getFileUrl(fileId);
    if (url) {
      setPreviewImage({
        url,
        title
      });
    }
  };
  
  // Update the view student modal to use file preview
  const ViewStudentModal = () => {
    if (!viewingStudent) return null;

    const formatDate = (dateString: string | null | undefined): string => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
      } catch (error) {
        console.error('Error formatting date:', error);
        return '';
      }
    };

    const renderPhoto = (fileId: string | null | undefined, title: string, alt: string) => {
      const photoUrl = fileId ? getFileUrl(fileId) : null;
      return (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-700">{title}</h4>
          <div 
            className="aspect-square w-full rounded-xl overflow-hidden bg-gray-100 shadow-md cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => fileId && handleFilePreview(fileId, title)}
          >
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={alt}
                className="w-full h-full object-cover"
                onError={(e) => {
                  console.error(`Error loading image for ${title}:`, e);
                  e.currentTarget.src = ''; // Clear the src on error
                  e.currentTarget.style.display = 'none'; // Hide the image
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                No photo available
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Student Details</h2>
            <button
              onClick={() => setViewingStudent(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Student Photo */}
            {renderPhoto(viewingStudent.student_photo_url, 'Student Photo', 'Student Photo')}

            {/* Birth Certificate */}
            {renderPhoto(viewingStudent.birth_certificate, 'Birth Certificate', 'Birth Certificate')}

            {/* Father's Photo */}
            {renderPhoto(viewingStudent.father_photo_url, 'Father\'s Photo', 'Father\'s Photo')}

            {/* Mother's Photo */}
            {renderPhoto(viewingStudent.mother_photo_url, 'Mother\'s Photo', 'Mother\'s Photo')}

            {/* Family Photo */}
            {renderPhoto(viewingStudent.family_photo_url, 'Family Photo', 'Family Photo')}

            {/* Guardian Photo */}
            {renderPhoto(viewingStudent.guardian_photo_url, 'Guardian\'s Photo', 'Guardian\'s Photo')}
          </div>

          {/* Rest of the student details */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Personal Information</h3>
              <p><span className="font-medium">Name:</span> {viewingStudent.name}</p>
              <p><span className="font-medium">Register Number:</span> {viewingStudent.register_num}</p>
              <p><span className="font-medium">Date of Birth:</span> {formatDate(viewingStudent.dob)}</p>
              <p><span className="font-medium">Gender:</span> {viewingStudent.gender}</p>
              <p><span className="font-medium">Level:</span> {viewingStudent.level}</p>
              <p><span className="font-medium">Group:</span> {viewingStudent.group}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Contact Information</h3>
              <p><span className="font-medium">Email:</span> {viewingStudent.email}</p>
              <p><span className="font-medium">Address:</span> {viewingStudent.address}</p>
              <p><span className="font-medium">Father's Name:</span> {viewingStudent.fathers_name}</p>
              <p><span className="font-medium">Father's Number:</span> {viewingStudent.father_num}</p>
              <p><span className="font-medium">Mother's Name:</span> {viewingStudent.mothers_name}</p>
              <p><span className="font-medium">Mother's Number:</span> {viewingStudent.mother_num}</p>
              <p><span className="font-medium">Guardian's Name:</span> {viewingStudent.guardian_name}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Additional Information</h3>
              <p><span className="font-medium">Cab Service:</span> {viewingStudent.cab === 'yes' ? 'Yes' : 'No'}</p>
              {viewingStudent.cab === 'yes' && (
                <p><span className="font-medium">Cab Location:</span> {viewingStudent.cab_loc}</p>
              )}
              <p><span className="font-medium">Day Care:</span> {viewingStudent.day_care === 'yes' ? 'Yes' : 'No'}</p>
              {viewingStudent.day_care === 'yes' && (
                <p><span className="font-medium">Day Care Time:</span> {viewingStudent.day_care_time}</p>
              )}
              <p><span className="font-medium">Special Instructions:</span> {viewingStudent.special_instruction}</p>
              <p><span className="font-medium">Allergies:</span> {viewingStudent.allergies}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Total Fees</h3>
              <p><span className="font-medium">Total Fees:</span> ₹{viewingStudent.total_fees}</p>
              <p><span className="font-medium">Total Day Care Fees:</span> ₹{viewingStudent.total_day_care_fees}</p>
              <p><span className="font-medium">Total Cab Fees:</span> ₹{viewingStudent.total_cab_fees}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Get available groups based on selected level
  const getAvailableGroups = () => {
    return [
      { value: 'larva1', label: 'Larva 1' },
      { value: 'larva2', label: 'Larva 2' },
      { value: 'larva3', label: 'Larva 3' },
      { value: 'pupa1', label: 'Pupa 1' },
      { value: 'pupa2', label: 'Pupa 2' },
      { value: 'pupa3', label: 'Pupa 3' },
      { value: 'caterpillar1', label: 'Caterpillar 1' },
      { value: 'caterpillar2', label: 'Caterpillar 2' },
      { value: 'caterpillar3', label: 'Caterpillar 3' },
      { value: 'butterfly', label: 'Butterfly' }
    ];
  };

  // Reset group selection when level changes
  useEffect(() => {
    setSelectedGroup('all');
  }, [selectedGrade]);

  const renderStudentList = () => {
    const formatDate = (dateString: string | null | undefined): string => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
      } catch (error) {
        console.error('Error formatting date:', error);
        return '';
      }
    };

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Photo
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Register Number
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date of Birth
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Gender
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Level
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Group
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredStudents.map((student) => {
              const photoUrl = student.student_photo_url ? getFileUrl(student.student_photo_url) : null;
              console.log('Student photo URL:', { student: student.name, photoUrl, fileId: student.student_photo_url });
              
              return (
                <tr key={student.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex-shrink-0 h-10 w-10">
                      {photoUrl ? (
                        <img
                          className="h-10 w-10 rounded-full object-cover"
                          src={photoUrl}
                          alt={student.name}
                          onError={(e) => {
                            console.error('Error loading image for student:', student.name, e);
                            const target = e.target as HTMLImageElement;
                            target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(student.name)}&background=random`;
                          }}
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-500 text-sm">
                            {student.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{student.name}</div>
                    <div className="text-sm text-gray-500">{student.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{student.register_num}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(student.dob)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{student.gender}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{student.level}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{student.group}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleViewClick(student)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleEditClick(student)}
                      className="text-pink-600 hover:text-pink-900 mr-4"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteStudent(student)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-100 p-4 rounded-md text-red-700">
        {error}
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Student Management</h1>
        <div className="flex gap-4">
          <button
            onClick={handleExport}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <Download className="h-5 w-5" />
            Export Selected
          </button>
          <button
            onClick={() => setShowAddStudentModal(true)}
            className="bg-pink-600 text-white px-4 py-2 rounded-md hover:bg-pink-700 transition-colors"
          >
            Add New Student
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Level Filter */}
          <div>
            <select
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Levels</option>
              <option value="1">Level 1</option>
              <option value="2">Level 2</option>
              <option value="3">Level 3</option>
              <option value="4">Level 4</option>
            </select>
          </div>

          {/* Group Filter */}
          <div>
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Groups</option>
              {getAvailableGroups().map(group => (
                <option key={group.value} value={group.value}>
                  {group.label}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters Button */}
          <div>
            <button
              onClick={() => {
                setSearch('');
                setSelectedGrade('all');
                setSelectedGroup('all');
              }}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
            >
              <Filter className="h-5 w-5" />
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
          {error}
        </div>
      )}

      {renderStudentList()}

      {/* View Student Modal */}
      {showViewModal && viewingStudent && (
        <ViewStudentModal />
      )}

      {/* Image Preview Modal */}
      <ImagePreviewModal />

      {/* Add Student Modal */}
      {showAddStudentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center overflow-y-auto pt-16 pb-8">
          <div className="bg-white p-6 rounded-lg w-[800px] relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => {
                setShowAddStudentModal(false);
                resetForm();
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-xl font-bold mb-4">Add New Student</h2>
            <form onSubmit={handleAddStudent} className="space-y-6">
              {/* Basic Information */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange(e, 'name')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange(e, 'email')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Register Number</label>
                    <input
                      type="text"
                      value={formData.register_num}
                      onChange={(e) => handleInputChange(e, 'register_num')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                    <input
                      type="date"
                      value={formatDobForInput(formData.dob)}
                      onChange={(e) => handleInputChange(e, 'dob')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Gender</label>
                    <select
                      value={formData.gender}
                      onChange={(e) => handleInputChange(e, 'gender')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Level</label>
                    <select
                      value={formData.level}
                      onChange={(e) => handleInputChange(e, 'level')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Group</label>
                    <select
                      value={formData.group}
                      onChange={(e) => handleInputChange(e, 'group')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="larva1">Larva 1</option>
                      <option value="larva2">Larva 2</option>
                      <option value="larva3">Larva 3</option>
                      <option value="pupa1">Pupa 1</option>
                      <option value="pupa2">Pupa 2</option>
                      <option value="pupa3">Pupa 3</option>
                      <option value="caterpillar1">Caterpillar 1</option>
                      <option value="caterpillar2">Caterpillar 2</option>
                      <option value="caterpillar3">Caterpillar 3</option>
                      <option value="butterfly">Butterfly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Address</label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => handleInputChange(e, 'address')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Parents Information */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Parents Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Father's Name</label>
                    <input
                      type="text"
                      value={formData.fathers_name}
                      onChange={(e) => handleInputChange(e, 'fathers_name')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Father's Mobile</label>
                    <input
                      type="tel"
                      value={formData.father_num}
                      onChange={(e) => handleInputChange(e, 'father_num')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Mother's Name</label>
                    <input
                      type="text"
                      value={formData.mothers_name}
                      onChange={(e) => handleInputChange(e, 'mothers_name')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Mother's Mobile</label>
                    <input
                      type="tel"
                      value={formData.mother_num}
                      onChange={(e) => handleInputChange(e, 'mother_num')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Guardian's Name</label>
                    <input
                      type="text"
                      value={formData.guardian_name}
                      onChange={(e) => handleInputChange(e, 'guardian_name')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Transportation Information */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Transportation Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Need Cab Service?</label>
                    <div className="flex space-x-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="cab"
                          value="yes"
                          checked={formData.cab === 'yes'}
                          onChange={() => handleNeedsCabChange(true)}
                          className="form-radio h-4 w-4 text-pink-600"
                        />
                        <span className="ml-2">Yes</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="cab"
                          value="no"
                          checked={formData.cab === 'no'}
                          onChange={() => handleNeedsCabChange(false)}
                          className="form-radio h-4 w-4 text-pink-600"
                        />
                        <span className="ml-2">No</span>
                      </label>
                    </div>
                  </div>
                  {formData.cab === 'yes' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Cab Location</label>
                      <input
                        type="text"
                        value={formData.cab_loc}
                        onChange={(e) => handleInputChange(e, 'cab_loc')}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Information */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Additional Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Need Day Care?</label>
                    <div className="flex space-x-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="day_care"
                          value="yes"
                          checked={formData.day_care === 'yes'}
                          onChange={() => handleNeedsDayCareChange(true)}
                          className="form-radio h-4 w-4 text-pink-600"
                        />
                        <span className="ml-2">Yes</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="day_care"
                          value="no"
                          checked={formData.day_care === 'no'}
                          onChange={() => handleNeedsDayCareChange(false)}
                          className="form-radio h-4 w-4 text-pink-600"
                        />
                        <span className="ml-2">No</span>
                      </label>
                    </div>
                  </div>
                  {formData.day_care === 'yes' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Day Care Timings</label>
                      <input
                        type="text"
                        value={formData.day_care_time}
                        onChange={(e) => handleInputChange(e, 'day_care_time')}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Special Instructions</label>
                    <textarea
                      value={formData.special_instruction}
                      onChange={(e) => handleInputChange(e, 'special_instruction')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Fees Section */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Fees</h3>
                <div>

                  <label htmlFor="total_fees" className="block text-sm font-medium text-gray-700">Total Fees</label>
                  <input
                    type="text"
                    id="total_fees"
                    name="total_fees"
                    value={formData.total_fees}
                    onChange={e => handleInputChange(e, 'total_fees')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    pattern="^[0-9]*$"
                  />
                  
                </div>
                <div className="mt-4">
                  <label htmlFor="total_day_care_fees" className="block text-sm font-medium text-gray-700">Total Day Care Fees</label>
                    <input
                      type="text"
                      id="total_day_care_fees"
                      name="total_day_care_fees"
                      value={formData.total_day_care_fees}
                      onChange={e => handleInputChange(e, 'total_day_care_fees')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      pattern="^[0-9]*$"
                    />
                  </div>
                  <div className="mt-4">
                    <label htmlFor="total_cab_fees" className="block text-sm font-medium text-gray-700">Total Cab Fees</label>
                    <input
                      type="text"
                      id="total_cab_fees"
                      name="total_cab_fees"
                      value={formData.total_cab_fees}
                      onChange={e => handleInputChange(e, 'total_cab_fees')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      pattern="^[0-9]*$"
                    />
                    </div>
              </div>

              {/* Photo Upload Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {renderPhotoPreview('student_photo_url', 'Student Photo')}
                {renderPhotoPreview('father_photo_url', 'Father Photo')}
                {renderPhotoPreview('mother_photo_url', 'Mother Photo')}
                {renderPhotoPreview('family_photo_url', 'Family Photo')}
                {renderPhotoPreview('guardian_photo_url', 'Guardian Photo')}
                {renderPhotoPreview('birth_certificate', 'Birth Certificate')}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddStudentModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-pink-600 rounded-md hover:bg-pink-700 disabled:opacity-50"
                >
                  {submitting ? 'Adding...' : 'Add Student'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Student Modal */}
      {showEditStudentModal && editingStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center overflow-y-auto pt-16 pb-8">
          <div className="bg-white p-6 rounded-lg w-[800px] relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => {
                setShowEditStudentModal(false);
                setEditingStudent(null);
                resetForm();
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-xl font-bold mb-4">Edit Student</h2>
            <form onSubmit={handleUpdateStudent} className="space-y-6">
              {/* Basic Information */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange(e, 'name')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange(e, 'email')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Register Number</label>
                    <input
                      type="text"
                      value={formData.register_num}
                      onChange={(e) => handleInputChange(e, 'register_num')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                    <input
                      type="date"
                      value={formatDobForInput(formData.dob)}
                      onChange={(e) => handleInputChange(e, 'dob')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Gender</label>
                    <select
                      value={formData.gender}
                      onChange={(e) => handleInputChange(e, 'gender')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Level</label>
                    <select
                      value={formData.level}
                      onChange={(e) => handleInputChange(e, 'level')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Group</label>
                    <select
                      value={formData.group}
                      onChange={(e) => handleInputChange(e, 'group')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="larva1">Larva 1</option>
                      <option value="larva2">Larva 2</option>
                      <option value="larva3">Larva 3</option>
                      <option value="pupa1">Pupa 1</option>
                      <option value="pupa2">Pupa 2</option>
                      <option value="pupa3">Pupa 3</option>
                      <option value="caterpillar1">Caterpillar 1</option>
                      <option value="caterpillar2">Caterpillar 2</option>
                      <option value="caterpillar3">Caterpillar 3</option>
                      <option value="butterfly">Butterfly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Address</label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => handleInputChange(e, 'address')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Parents Information */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Parents Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Father's Name</label>
                    <input
                      type="text"
                      value={formData.fathers_name}
                      onChange={(e) => handleInputChange(e, 'fathers_name')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Father's Mobile</label>
                    <input
                      type="tel"
                      value={formData.father_num}
                      onChange={(e) => handleInputChange(e, 'father_num')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Mother's Name</label>
                    <input
                      type="text"
                      value={formData.mothers_name}
                      onChange={(e) => handleInputChange(e, 'mothers_name')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Mother's Mobile</label>
                    <input
                      type="tel"
                      value={formData.mother_num}
                      onChange={(e) => handleInputChange(e, 'mother_num')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Guardian's Name</label>
                    <input
                      type="text"
                      value={formData.guardian_name}
                      onChange={(e) => handleInputChange(e, 'guardian_name')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Transportation Information */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Transportation Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Need Cab Service?</label>
                    <div className="flex space-x-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="cab"
                          value="yes"
                          checked={formData.cab === 'yes'}
                          onChange={(e) => handleNeedsCabChange(true)}
                          className="form-radio h-4 w-4 text-pink-600"
                        />
                        <span className="ml-2">Yes</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="cab"
                          value="no"
                          checked={formData.cab === 'no'}
                          onChange={(e) => handleNeedsCabChange(false)}
                          className="form-radio h-4 w-4 text-pink-600"
                        />
                        <span className="ml-2">No</span>
                      </label>
                    </div>
                  </div>
                  {formData.cab === 'yes' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Cab Location</label>
                      <input
                        type="text"
                        value={formData.cab_loc}
                        onChange={(e) => handleInputChange(e, 'cab_loc')}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Information */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Additional Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Need Day Care?</label>
                    <div className="flex space-x-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="day_care"
                          value="yes"
                          checked={formData.day_care === 'yes'}
                          onChange={(e) => handleNeedsDayCareChange(true)}
                          className="form-radio h-4 w-4 text-pink-600"
                        />
                        <span className="ml-2">Yes</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="day_care"
                          value="no"
                          checked={formData.day_care === 'no'}
                          onChange={(e) => handleNeedsDayCareChange(false)}
                          className="form-radio h-4 w-4 text-pink-600"
                        />
                        <span className="ml-2">No</span>
                      </label>
                    </div>
                  </div>
                  {formData.day_care === 'yes' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Day Care Timings</label>
                      <input
                        type="text"
                        value={formData.day_care_time}
                        onChange={(e) => handleInputChange(e, 'day_care_time')}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Special Instructions</label>
                    <textarea
                      value={formData.special_instruction}
                      onChange={(e) => handleInputChange(e, 'special_instruction')}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Fees Section */}
              <div className="border-b pb-4">
                <h3 className="text-lg font-semibold mb-3">Fees</h3>
                <div>
                  <label htmlFor="total_fees" className="block text-sm font-medium text-gray-700">Total Fees</label>
                  <input
                    type="text"
                    id="total_fees"
                    name="total_fees"
                    value={formData.total_fees}
                    onChange={e => handleInputChange(e, 'total_fees')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    pattern="^[0-9]*$"
                  />
                </div>
                <div>
                  <label htmlFor="total_day_care_fees" className="block text-sm font-medium text-gray-700">Total Day Care Fees</label>
                  <input
                    type="text"
                    id="total_day_care_fees"
                    name="total_day_care_fees"
                    value={formData.total_day_care_fees}
                    onChange={e => handleInputChange(e, 'total_day_care_fees')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    pattern="^[0-9]*$"
                  />
                </div>
                <div>
                  <label htmlFor="total_cab_fees" className="block text-sm font-medium text-gray-700">Total Cab Fees</label>
                  <input
                    type="text"
                    id="total_cab_fees"
                    name="total_cab_fees"
                    value={formData.total_cab_fees}
                    onChange={e => handleInputChange(e, 'total_cab_fees')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    pattern="^[0-9]*$"
                  />
                </div>
              </div>

              {/* Photo Upload Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {renderPhotoPreview('student_photo_url', 'Student Photo')}
                {renderPhotoPreview('father_photo_url', 'Father Photo')}
                {renderPhotoPreview('mother_photo_url', 'Mother Photo')}
                {renderPhotoPreview('family_photo_url', 'Family Photo')}
                {renderPhotoPreview('guardian_photo_url', 'Guardian Photo')}
                {renderPhotoPreview('birth_certificate', 'Birth Certificate')}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditStudentModal(false);
                    setEditingStudent(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentPage;